import React, { useState } from 'react';
import { useProperty } from '../context/PropertyContext';
import { useUser } from '../context/UserContext';
import { emailService } from '../services/emailService';
import { Lead, Property } from '../types';
import { 
  X, 
  Mail, 
  Heart, 
  Search, 
  Home,
  Send,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

interface EmailOptionsModalProps {
  lead: Lead;
  onClose: () => void;
  onEmailSent: (lead: Lead, emailType: string) => void;
}

const EmailOptionsModal: React.FC<EmailOptionsModalProps> = ({ lead, onClose, onEmailSent }) => {
  const { properties } = useProperty();
  const { users } = useUser();
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPropertySelector, setShowPropertySelector] = useState(false);

  // Encontrar imóveis que combinam com o lead
  const matchingProperties = emailService.findMatchingProperties(lead, properties);

  // Pegar corretor aleatório (excluindo admin)
  const getRandomBroker = () => {
    const brokers = users.filter(user => user.id !== '1'); // Excluir admin
    if (brokers.length === 0) return users[0]; // Fallback para admin se não houver outros
    return brokers[Math.floor(Math.random() * brokers.length)];
  };

  const emailOptions = [
    {
      id: 'property-recommendation',
      title: 'Imóvel Recomendado',
      description: 'Envie imóveis que combinam com o interesse do lead',
      icon: Home,
      color: 'bg-blue-500',
      hoverColor: 'hover:bg-blue-600',
      requiresProperty: true,
      available: matchingProperties.length > 0
    },
    {
      id: 'miss-you',
      title: 'Sentimos sua Falta',
      description: 'Email carinhoso dizendo que sentimos falta e queremos ajudar',
      icon: Heart,
      color: 'bg-pink-500',
      hoverColor: 'hover:bg-pink-600',
      requiresProperty: false,
      available: true
    },
    {
      id: 'still-looking',
      title: 'Ainda está Procurando?',
      description: 'Pergunte se ainda procura imóveis e ofereça novas oportunidades',
      icon: Search,
      color: 'bg-purple-500',
      hoverColor: 'hover:bg-purple-600',
      requiresProperty: false,
      available: true
    }
  ];

  const handleOptionSelect = (optionId: string) => {
    const option = emailOptions.find(opt => opt.id === optionId);
    
    if (option?.requiresProperty && matchingProperties.length > 0) {
      setSelectedOption(optionId);
      setShowPropertySelector(true);
    } else {
      setSelectedOption(optionId);
      setShowPropertySelector(false);
    }
  };

  const handleSendEmail = async () => {
    if (!selectedOption || !lead.email) return;

    setLoading(true);

    try {
      let result;
      const randomBroker = getRandomBroker();

      switch (selectedOption) {
        case 'property-recommendation':
          if (!selectedProperty) {
            alert('Por favor, selecione um imóvel');
            setLoading(false);
            return;
          }
          result = await emailService.sendPropertyRecommendationEmail(lead, selectedProperty);
          break;

        case 'miss-you':
          result = await emailService.sendMissYouEmail(lead, randomBroker);
          break;

        case 'still-looking':
          result = await emailService.sendStillLookingEmail(lead, randomBroker);
          break;

        default:
          throw new Error('Tipo de email inválido');
      }

      if (result.success) {
        alert(`Email "${emailOptions.find(opt => opt.id === selectedOption)?.title}" enviado com sucesso!`);
        onEmailSent(lead, selectedOption);
        onClose();
      } else {
        alert('Erro ao enviar email. Tente novamente.');
        console.error('Email error:', result.error);
      }
    } catch (error) {
      alert('Erro ao enviar email. Verifique sua configuração.');
      console.error('Send email error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (showPropertySelector) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[98vh] sm:h-[95vh] lg:h-[90vh] flex flex-col">
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-800">
              Selecionar Imóvel para Recomendar
            </h2>
            <button
              onClick={() => setShowPropertySelector(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            <div className="mb-4">
              <p className="text-gray-600">
                Selecione o imóvel que melhor combina com o interesse de <strong>{lead.name}</strong>:
              </p>
              <div className="bg-gray-50 rounded-lg p-3 mt-2">
                <p className="text-sm text-gray-700 italic">"{lead.message}"</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {matchingProperties.map((property) => (
                <div
                  key={property.listingId}
                  onClick={() => setSelectedProperty(property)}
                  className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                    selectedProperty?.listingId === property.listingId
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <img
                    src={property.media.images[0]?.url || 'https://images.pexels.com/photos/106399/pexels-photo-106399.jpeg?auto=compress&cs=tinysrgb&w=800'}
                    alt={property.title}
                    className="w-full h-32 object-cover rounded-lg mb-3"
                  />
                  <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2">
                    {property.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-2">
                    {property.location.neighborhood}, {property.location.city}
                  </p>
                  <p className="text-lg font-bold text-blue-600">
                    {new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                      minimumFractionDigits: 0
                    }).format(property.listPrice)}
                  </p>
                  <div className="flex items-center space-x-3 text-sm text-gray-500 mt-2">
                    <span>{property.bedrooms} quartos</span>
                    <span>{property.garage.quantity} vagas</span>
                    <span>{property.livingArea}m²</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowPropertySelector(false)}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Voltar
              </button>
              <button
                onClick={handleSendEmail}
                disabled={!selectedProperty || loading}
                className="bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    <span>Enviar Email</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[98vh] sm:max-h-[95vh] lg:max-h-[90vh] flex flex-col">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-800">
            Enviar Email para {lead.name}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {/* Lead Info */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-3 mb-2">
              <Mail className="w-5 h-5 text-gray-500" />
              <span className="font-medium text-gray-900">{lead.email}</span>
            </div>
            <p className="text-sm text-gray-600 italic">"{lead.message}"</p>
          </div>

          {/* Email Options */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Escolha o tipo de email:
            </h3>

            {emailOptions.map((option) => {
              const Icon = option.icon;
              const isDisabled = !option.available;
              
              return (
                <button
                  key={option.id}
                  onClick={() => !isDisabled && handleOptionSelect(option.id)}
                  disabled={isDisabled}
                  className={`w-full p-4 border-2 rounded-lg text-left transition-all ${
                    selectedOption === option.id
                      ? 'border-orange-500 bg-orange-50'
                      : isDisabled
                      ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start space-x-4">
                    <div className={`p-3 rounded-full ${option.color} ${!isDisabled && option.hoverColor}`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-gray-900 mb-1">
                        {option.title}
                        {option.requiresProperty && (
                          <span className="ml-2 text-sm text-blue-600">
                            ({matchingProperties.length} imóveis disponíveis)
                          </span>
                        )}
                      </h4>
                      <p className="text-gray-600">{option.description}</p>
                      {isDisabled && option.requiresProperty && (
                        <div className="flex items-center space-x-2 mt-2">
                          <AlertCircle className="w-4 h-4 text-red-500" />
                          <span className="text-sm text-red-600">
                            Nenhum imóvel encontrado que combine com este lead
                          </span>
                        </div>
                      )}
                    </div>
                    {selectedOption === option.id && (
                      <CheckCircle className="w-6 h-6 text-orange-500" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

          {/* Actions */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
          <div className="flex justify-end space-x-4">
            <button
              onClick={onClose}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleSendEmail}
              disabled={!selectedOption || loading}
              className="bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  <span>Enviar Email</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailOptionsModal;